import { useDispatch, useSelector } from "react-redux";
import ChatInput from "../../AmplitudeAI/Chat/ChatInput";
import PageDescription from "../../DataAnalyst/sections/PageDescription";
import MixesFileUpload from "./MixesFileUpload";
import { consumeCredits, sendMessage } from "../../../../redux/actions";
import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "../../../../helper/ShowToast";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigationGuard } from "../../../../hooks/useNavigationGuard";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";
import { CreditPlanModal } from "../../../../components/CreditPlanModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MasteredSection from "./MasteredSection";
import { MixReport } from "./MixReport";
import { clearUploadData, processUploadedFiles, setUploadFileData, uploadFilesToS3 } from "../../../../redux/actions/audioEngineer";
import { useCreateMixAnalysisReport } from "../../../../queries/urstudio/mixanalyst.query";
import { MixAnalystPackModal } from "./MixAnalystPackModal";
import { addProcess, completeProcess, failProcess } from "../../../../redux/actions/backgroundProcesses";
import AppTour from "../../../../components/AppTour/AppTour";
import { mixAnalystTourSteps } from "../../../../components/AppTour/ModuleTourSteps";

export default function MixAnalyst() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pageTab = params.get("tab");
    const [runTour, setRunTour] = useState(false)
    const [showChargeModal, setShowChargeModal] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [isReportGenerated, setIsReportGenerated] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [mixReportData, setMixReportData] = useState(null);
    const [showFileUpload, setShowFileUpload] = useState(true)
    const [showQnA, setShowQnA] = useState(false)
    const [uploadedFileData, setUploadedFileData] = useState(null)
    const [isMastered, setIsMastered] = useState(false)
    const [isNext, setIsNext] = useState(false);
    const [isLoading, setIsLoading] = useState(null);
    const [reqData, setReqData] = useState(null)

    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const { uploadedData, uploadedFiles } = useSelector((state) => state.audioEngineer);
    const awaitingMasteringUpload = useRef(false);
    const isStartTour = location.hash === "#start-tour"

    const handleUploadNew = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        navigate("/admin/urstudio/mix-analyst")
        dispatch(clearUploadData({}));
        setShowFileUpload(true);
        setShowQnA(false);
        setIsNext(false);
    };

    const startMixAnalysis = () => {
        setShowFileUpload(false)
        setShowQnA(true)
        navigate("/admin/urstudio/mix-analyst?tab=settings")
    }

    useEffect(() => {
        if (pageTab !== "upload" && showFileUpload && (uploadedData?.data?.[0]?.roexStatus === 'LOCAL_UPLOADED' || uploadedData?.data?.[0]?.roexStatus === 'UPLOADED_TO_ROEX')) {
            dispatch(clearUploadData());
        }
    }, [dispatch, pageTab])

    useEffect(() => {
        if (awaitingMasteringUpload.current && uploadedData?.data?.[0]?.roexStatus === 'UPLOADED_TO_ROEX') {
            awaitingMasteringUpload.current = false;
        }
    }, [uploadedData]);

    const handleFileSelect = useCallback(async (file) => {
        const fileArray = Array.isArray(file) ? file : (file && file.length ? Array.from(file) : [file]);

        // Validate files
        const validFiles = fileArray.filter(f => f && f instanceof File);

        if (validFiles.length === 0) {
            showToast("error", "No valid files selected");
            return;
        }

        // Pass array of File objects directly (NOT FormData)
        dispatch(uploadFilesToS3(
            validFiles,
            setUploadComplete,
            (successfulUploads) => {
                setUploadedFileData(successfulUploads)
                dispatch(setUploadFileData(successfulUploads))
            }
        ));
    }, [dispatch, uploadedFileData, uploadedFiles]);

    const handleSend = (input) => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);
            return;
        }

        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true);
            return;
        }

        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    const mutateMixAnalysis = useCreateMixAnalysisReport()
    const { isPending, isSuccess, isError } = mutateMixAnalysis

    const isProcessing = !!(isPending || isLoading);

    // Block browser back/forward and app back navigation only while processing
    useNavigationGuard(
        isProcessing,
        "Mix analysis is in progress. Are you sure you want to leave? Your progress may be lost."
    );

    // Warn on tab close/refresh only
    useEffect(() => {
        if (!isProcessing) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isProcessing]);

    const handleNext = useCallback((e) => {
        setIsReportGenerated(false)
        setIsMastered(e.isMastered)

        const file = uploadedFiles?.[0];

        if (!file) {
            showToast("error", "No uploaded file data found");
            return;
        }

        const ext = file.filename?.split('.').pop();

        // Create process first to ensure proper notification on completion
        const processIdAction = dispatch(addProcess({
            name: `Mix Analyst File Report of ${uploadedFiles?.length} file`,
            type: 'mix-analyst',
            metadata: { dataType: 'mix-report', fileCount: uploadedFiles?.length },
        }));
        const processId = processIdAction.payload.id;

        if (!e.isMastered) {
            const reqBody = {
                filename: file.filename,
                s3Url: file.s3Url,
                size: file.size,
                duration: file.duration,
                format: ext,
                mimeType: `audio/${ext}`,
                fileLocation: file.s3Url,
                musicalStyle: e.selectedGenre,
                isMaster: false,
                isMixed: true
            }
            setReqData(reqBody)

            if (hasActiveSubscription) {
                if (!creditDetails?.data || creditDetails?.data?.remaining <= 3) {
                    showToast("info", "You are out of AI credits. Please purchase more to continue.");
                    setShowCreditPlanModal(true);
                    dispatch(failProcess(processId, "Insufficient credits"));
                    return;
                }
                const uploadedFileInfo = [{
                    filename: file.filename,
                    s3Url: file.s3Url,
                    s3Key: file.s3Key,
                    contentType: file.contentType,
                    size: file.size,
                    duration: file.duration,
                    uploadSuccess: true,
                    fromUrl: false
                }]
                awaitingMasteringUpload.current = true;
                setIsLoading(true);
                let stopMasteringPolling = null;
                dispatch(processUploadedFiles(uploadedFileInfo, setIsLoading, 'mastering', (stop) => {
                    stopMasteringPolling = stop;
                }))

                mutateMixAnalysis.mutate({ ...reqBody, processId }, {
                    onSuccess: (data) => {
                        // dispatch(consumeCredits(3))
                        // dispatch(completeProcess(processId));
                        setMixReportData(data)
                        window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                        });
                    },
                    onError: (error) => {
                        dispatch(failProcess(processId, error?.message || "Mix analysis failed"));
                        if (stopMasteringPolling) stopMasteringPolling();
                        setIsLoading(false);
                    },
                    onSettled: () => {
                        setIsReportGenerated(true)
                    }
                })
            } else {
                setIsLoading(false);
                setShowChargeModal(true)
                // dispatch(failProcess(processId, "No active subscription"));
            }
        } else {
            const reqBody = {
                filename: file.filename,
                s3Url: file.s3Url,
                size: file.size,
                duration: file.duration,
                format: ext,
                mimeType: `audio/${ext}`,
                fileLocation: file.s3Url,
                musicalStyle: e.selectedGenre,
                isMaster: true,
                isMixed: false
            }
            setReqData(reqBody)

            if (hasActiveSubscription) {
                if (!creditDetails?.data || creditDetails?.data?.remaining <= 3) {
                    showToast("info", "You are out of AI credits. Please purchase more to continue.");
                    setShowCreditPlanModal(true);
                    dispatch(failProcess(processId, "Insufficient credits"));
                    return;
                }
                mutateMixAnalysis.mutate({ ...reqBody, processId }, {
                    onSuccess: (data) => {
                        // dispatch(consumeCredits(3))
                        // dispatch(completeProcess(processId));
                        setMixReportData(data)
                        window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                        });
                    },
                    onError: (error) => {
                        dispatch(failProcess(processId, error?.message || "Mix analysis failed"));
                    },
                    onSettled: () => {
                        setIsReportGenerated(true)
                    }
                })
            } else {
                setIsLoading(false);
                setShowChargeModal(true)
                // dispatch(failProcess(processId, "No active subscription"));
            }
        }
    }, [dispatch, uploadedFiles, uploadedData, mutateMixAnalysis]);

    useEffect(() => {
        if (isMastered) {
            if (!isPending && isPending !== null) {
                if (isReportGenerated) {
                    setShowQnA(false);
                    setIsNext(true);
                    navigate("/admin/urstudio/mix-analyst?tab=report");
                }
            }
        } else {
            if (!isLoading && !isPending && (isLoading !== null && isPending !== null)) {
                if (isReportGenerated) {
                    setShowQnA(false);
                    setIsNext(true);
                    navigate("/admin/urstudio/mix-analyst?tab=report");
                }
            }
        }
    }, [isLoading, isPending, isReportGenerated, isMastered, navigate]);

    const handleReportReady = useCallback((reportData) => {
        setMixReportData(reportData);
        setShowQnA(false);
        setIsNext(true);
    }, []);

    useEffect(() => {
        if (isStartTour) {
            setRunTour(true)
        }
    }, [location.hash])

    useEffect(() => {
        if (pageTab === "upload") {
            setShowFileUpload(true);
            setShowQnA(false)
        }
        if (pageTab === "settings") {
            setShowFileUpload(false)
            setShowQnA(true)
        }
        if (pageTab === "report") {
            setShowFileUpload(false)
            setShowQnA(false);
            setIsNext(true);
        }
    }, [pageTab]);

    return (
        <div className="DataAnalyst-page py-5">
            <ToastContainer />
            <AppTour
                tourSteps={mixAnalystTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <PageDescription
                    title={isStartTour ? "Your Mix Analyst - Demo Mode" : "Your Mix Analyst"}
                    description="Diagnose your mix with AI-powered insights to see what’s working and what needs work."
                />
                {showFileUpload && (
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
                        <MixesFileUpload onFileSelect={handleFileSelect} />
                        <div className="mt-4 mx-auto w-[40%] bg-black text-white text-center text-sm font-medium rounded-md px-4 py-2 flex justify-center items-center gap-2 shadow-md">
                            <span>
                                For Pro users, this will deduct 3 AI credits from your balance.
                            </span>
                        </div>
                        {uploadedData?.data?.length > 0 && uploadedData?.data?.[0]?.operation === 'mix-analysis' && (
                            <div className="flex flex-col w-[40%] mx-auto items-center mt-4 space-y-3">
                                {uploadedData.data.map((file, index) => {

                                    let progress = 30;
                                    let label = "30%";

                                    if (file.roexStatus === "LOCAL_UPLOADING") {
                                        progress = 40;
                                        label = "40%";
                                    }

                                    if (file.roexStatus === "LOCAL_UPLOADED") {
                                        progress = 100;
                                        label = "100%";
                                    }

                                    return (
                                        <div key={index} className="w-full">
                                            <div className="flex justify-between text-white text-sm mb-1">
                                                <span className="truncate max-w-[80%]">
                                                    {file.filename}
                                                </span>
                                                <span>{label}</span>
                                            </div>

                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-700 ${file.roexStatus === "FAILED"
                                                        ? "bg-red-500"
                                                        : file.roexStatus === "LOCAL_UPLOADED"
                                                            ? "bg-theme_clr2"
                                                            : "bg-theme_clr2 animated-pulse"
                                                        }`}
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>

                                            {file.roexStatus === "LOCAL_UPLOADED" && (
                                                <button
                                                    onClick={startMixAnalysis}
                                                    className="mt-6 w-full bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium"
                                                >
                                                    Start Mix Analysis
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {location.hash === "#start-tour" &&
                    <>
                        <div className="mt-4" data-tour="settings-section">
                            <MasteredSection
                                isLoading={false}
                                onNextClick={handleNext}
                            />
                        </div>
                        <div className="mt-4" data-tour="mix-report">
                            <MixReport
                                demoMode
                                isMastered={isMastered}
                                reportData={mixReportData}
                                onNewUpload={handleUploadNew}
                            />
                        </div>
                    </>
                }

                {showQnA && (
                    <MasteredSection
                        isLoading={isPending || isLoading}
                        onNextClick={handleNext}
                    />
                )}

                {isNext && !showQnA && (
                    <MixReport
                        isMastered={isMastered}
                        reportData={mixReportData}
                        onNewUpload={handleUploadNew}
                    />
                )}

                <div className="mt-4" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={'Plan your next move with your improved mixes.'}
                        inputPlaceholder={"Let Amplitude AI design the perfect release strategy."}
                        onSend={handleSend}
                        isDisabled={showLoad}
                    />
                </div>

                {isStartTour &&
                    <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                        <p className="text-blue-900 font-semibold mb-2">ℹ️ Demo Mode</p>
                        <p className="text-blue-800 text-sm">
                            This is a demonstration page showing the Mix Analyst interface. All data is simulated for demo purposes.
                        </p>
                    </div>
                }
            </div>
            <MixAnalystPackModal
                isOpen={showChargeModal}
                onClose={() => setShowChargeModal(false)}
                requestData={reqData}
                setMixReport={setMixReportData}
                setIsReportGenerated={setIsReportGenerated}
                fromLibrary={false}
                onReportReady={handleReportReady}
                uploadedFileData={uploadedFiles?.[0]}
                isMastered={isMastered}
            />
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/urstudio/mix-analyst"}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </div>
    )
}